import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { db } from '@/api/firebase';
import { useMainContext } from '@/context/main-context';

import BreadcrumbComponent from '@/components/breadcrumb';
import Attendance from '@/components/groups/attendance/matrix';
import Evaluation from '@/components/groups/evaluation/evaluation';
import Exams from '@/components/groups/exam/exams';
import GroupHeader from '@/components/groups/header';
import Tasks from '@/components/groups/tasks/tasks';
import StudentsDataTable from '@/components/students/data-table';
import GroupPaymentHistory from '@/components/groups/payment-history';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const Group = () => {
  const { groupId } = useParams();
  const { groups, adminId, groupStudents, setGroupStudents, students, courses } = useMainContext();
  const group = groups.find((g) => g.id === groupId);

  const [loadingStudents, setLoadingStudents] = useState(false);

  const fetchGroupStudents = useCallback(async () => {
    setLoadingStudents(true);
    try {
      const groupRef = doc(db, `users/${adminId}/groups`, groupId);
      const groupSnap = await getDoc(groupRef);

      if (groupSnap.exists()) {
        const groupData = groupSnap.data();
        const studentIds = groupData.students || [];

        if (studentIds.length > 0) {
          const studentsRef = collection(db, `students`);
          const q = query(studentsRef, where('__name__', 'in', studentIds));
          const querySnapshot = await getDocs(q);
          const fetchedStudents = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setGroupStudents(fetchedStudents);
        } else {
          setGroupStudents([]);
        }
      }
    } catch (error) {
      console.error('Error fetching group data:', error);
    } finally {
      setLoadingStudents(false);
    }
  }, [adminId, groupId, setGroupStudents]);

  useEffect(() => {
    if (groupId) {
      fetchGroupStudents();
    }
  }, [groupId, fetchGroupStudents]);

  if (!group) {
    return (
      <div className="px-4 lg:px-8 mx-auto py-4">
        <h2 className="text-2xl font-bold tracking-tight">404 error</h2>
        <p className="text-muted-foreground">
          Siz qidirayotgan guruh topilmadi!
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 lg:px-8 mt-4">
      <BreadcrumbComponent
        title="Guruhlar ro'yxati"
        titleLink="/groups"
        subtitle={group?.groupName}
      />
      <GroupHeader group={group} />

      <Tabs defaultValue="students" className="mt-4">
        <div className="overflow-x-auto w-[calc(100%-0px)]">
          <TabsList>
            <TabsTrigger value="students">O'quvchilar ro'yxati</TabsTrigger>
            <TabsTrigger value="absentee">Yo'qlamalar</TabsTrigger>
            <TabsTrigger value="evaluation">
              Baholash
            </TabsTrigger>
            <TabsTrigger className="hidden" value="tasks">
              Vazifalar
            </TabsTrigger>
            <TabsTrigger className="hidden" value="exams">
              Imtihonlar
            </TabsTrigger>
            <TabsTrigger value="payment-history">To'lovlar tarixi</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="students">
          <StudentsDataTable data={groupStudents} loading={loadingStudents} />
        </TabsContent>
        <TabsContent value="absentee">
          <Attendance group={group} students={groupStudents} />
        </TabsContent>
        <TabsContent value="payment-history">
          <GroupPaymentHistory
            students={students}
            group={group}
            courses={courses}
            groups={groups}
          />
        </TabsContent>
        <TabsContent value="tasks">
          <Tasks groupId={groupId} adminId={adminId} />
        </TabsContent>
        <TabsContent value="evaluation">
          <Evaluation groupId={groupId} students={groupStudents} />
        </TabsContent>
        <TabsContent value="exams">
          <Exams groupId={groupId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Group;
